import { useLayoutEffect, useRef, useState } from 'react';
import { Lightbulb, PenLine, Plug, Rocket, ShieldCheck } from 'lucide-react';
import { useMedia } from '@/hooks/useMedia';
import { useStage } from './guide/stage';
import { logContent } from './guide/beats';
import { useSectionEnter } from './guide/useSectionEnter';
import './how-it-works.css';

// Owner copy (2026-10-15): exploration + Studio curation, replay validator with narration
// fallback, public entry / walkthroughs / widget, insights back to the teams.
const steps = [
  [Plug, 'Connect', 'Point Foxet at your product with a sandbox login.'],
  [PenLine, 'Teach & review', 'Foxet explores and drafts flows; your team curates and adds knowledge.'],
  [ShieldCheck, 'Verify', 'Every flow is replayed against the live product; stale ones fall back to narration.'],
  [Rocket, 'Publish', 'Your expert goes live on your site, in sessions and walkthroughs.'],
  [Lightbulb, 'Learn', 'Questions and friction come back to Product and GTM.'],
] as const;
const PER_STEP = 1200;
const RETURN_MS = 2400; // the dot's lap along the return path (slower than the forward line)
const IDLE_LAP_MS = 12000; // then one lap every ~12s while in view
const LABEL = 'Continuous learning · what buyers ask and where they stumble goes back into the flows';

// B17 (content): the stepper is a feedback loop, not a line (owner, 2026-10-16). On entry (the section taking his
// eyes) the five steps light in turn — icon ember, title ink, a 1px ember progress along the line to the next dot —
// 1.2s per step, once; then the return runs: a hairline drops from the Learn dot, runs right→left under the
// descriptions and rises into the Teach & review dot with an arrowhead, and the same ember dot travels it once,
// slower; then the loop idles at one lap every ~12s while in view. Mr. Clippy's eyes follow the lit step and the dot
// on its return lap; on Verify a squint then one nod; on Learn a small lean back. Reduced motion: all lit at once,
// the return path drawn, no dot.
export function HowItWorks() {
  const section = useRef<HTMLElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const dots = useRef<(HTMLSpanElement | null)[]>([]);
  const titles = useRef<(HTMLHeadingElement | null)[]>([]);
  const runner = useRef<SVGCircleElement>(null);
  const anim = useRef<SVGAnimateMotionElement>(null);
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const desktop = useMedia('(min-width: 1000px)');
  const stage = useStage();
  const [lit, setLit] = useState(0); // steps lit so far (0 = none, before entry)
  const [progress, setProgress] = useState(0); // px along the line
  const [geo, setGeo] = useState<{ w: number; h: number; d: string; mid: { x: number; y: number } } | null>(null);
  const [lap, setLap] = useState(0); // > 0: the return dot runs (a new key per lap)
  const [inView, setInView] = useState(false);
  const started = useRef(false);
  const timers = useRef<number[]>([]);

  // the return path: from the Learn dot down the gap left of Learn, right→left along the band below the descriptions,
  // up the gap left of Teach & review, into its dot (arrowhead); measured from the dots and the list's box
  useLayoutEffect(() => {
    const l = list.current;
    if (!l || !desktop) { setGeo(null); return; }
    const measure = () => {
      const lb = l.getBoundingClientRect();
      const d = (i: number) => { const el = dots.current[i]; if (!el) return null; const r = el.getBoundingClientRect(); return { x: r.left - lb.left + r.width / 2, y: r.top - lb.top + r.height / 2 }; };
      const learn = d(4); const teach = d(1);
      if (!learn || !teach) return;
      const bandY = lb.height + 36; // the band below the descriptions
      const path = `M ${learn.x} ${learn.y + 7} L ${learn.x - 10} ${learn.y + 7} L ${learn.x - 10} ${bandY} L ${teach.x - 10} ${bandY} L ${teach.x - 10} ${teach.y} L ${teach.x - 7} ${teach.y}`;
      setGeo({ w: lb.width, h: bandY + 8, d: path, mid: { x: (learn.x + teach.x) / 2 - 10, y: bandY } });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(l);
    return () => ro.disconnect();
  }, [desktop, lit]);
  // the loop idles while the section is in view
  useLayoutEffect(() => {
    const el = section.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting && e.intersectionRatio >= 0.4), { threshold: [0, 0.4] });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const idle = useRef(0);
  useLayoutEffect(() => {
    if (!started.current || reduced || !inView || lit < steps.length) { if (idle.current) { window.clearInterval(idle.current); idle.current = 0; } return; }
    idle.current = window.setInterval(() => setLap((k) => k + 1), IDLE_LAP_MS);
    return () => { if (idle.current) { window.clearInterval(idle.current); idle.current = 0; } };
  }, [inView, lit, reduced]);
  // each lap begins by hand (SMIL time runs from document load, so a fixed `begin` would jump to the path's end)
  useLayoutEffect(() => { if (lap > 0) anim.current?.beginElement(); }, [lap]);
  // B17: his eyes follow the dot along the return lap
  const followDot = () => {
    const t0 = performance.now();
    const tick = () => { if (performance.now() - t0 > RETURN_MS) { stage?.react('.fx-how', 'clippy', { lookAt: titles.current[1] }); return; } stage?.react('.fx-how', 'clippy', { lookAt: runner.current }); window.setTimeout(tick, 160); };
    tick();
  };
  useSectionEnter(section, '.fx-how', () => {
    started.current = true;
    logContent('C17', 'the five steps light in turn, then the return runs');
    if (reduced) { setLit(steps.length); return; }
    const lineX = (i: number) => { const d = dots.current[i]; const l = list.current; return d && l ? d.getBoundingClientRect().left - l.getBoundingClientRect().left + d.offsetWidth / 2 : 0; };
    steps.forEach((_, i) => {
      timers.current.push(window.setTimeout(() => {
        setLit(i + 1);
        setProgress(lineX(i));
        stage?.react('.fx-how', 'clippy', { lookAt: titles.current[i] });
        if (i === 2) { stage?.react('.fx-how', 'clippy', { eyes: { mode: 'squint', key: 'verify' } }); timers.current.push(window.setTimeout(() => stage?.react('.fx-how', 'clippy', { nod: true }), 450)); } // a squint (the lids, not a look), then one nod
        if (i === 4) { stage?.react('.fx-how', 'clippy', { leanBack: true, lookAt: titles.current[i] }); timers.current.push(window.setTimeout(() => { setLap(1); followDot(); }, 600)); } // then the return runs once
      }, i * PER_STEP));
    });
  });
  return (
    <section className={`fx-how fx-rv${started.current || lit ? ' fx-how-live' : ''}`} id="how-it-works" ref={section} data-lit={lit} data-lap={lap}>
      <div className="container">
        <ol className="fx-how-steps" ref={list}>
          {!reduced && <span className="fx-how-progress" aria-hidden="true" style={{ width: progress }} />}
          {steps.map(([Icon, title, text], i) => (
            <li key={title} className={i < lit ? 'lit' : undefined}>
              <span className="fx-how-dot" aria-hidden="true" ref={(el) => { dots.current[i] = el; }} />
              <Icon className="fx-how-icon" size={18} aria-hidden="true" />
              <h3 ref={(el) => { titles.current[i] = el; }}>{title}</h3>
              <p>{text}</p>
            </li>
          ))}
          {geo && (
            <svg className={`fx-how-return${lit === steps.length ? ' lit' : ''}`} width={geo.w} height={geo.h} viewBox={`0 0 ${geo.w} ${geo.h}`} aria-hidden="true">
              <defs>
                <marker id="fx-how-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="userSpaceOnUse">
                  <path d="M 0 0 L 6 3 L 0 6" />
                </marker>
              </defs>
              <path id="fx-how-return-path" d={geo.d} markerEnd="url(#fx-how-arrow)" />
              {!reduced && lap > 0 && (
                <circle r="4" className="fx-how-runner" ref={runner} key={lap}>
                  <animateMotion ref={anim} dur={`${RETURN_MS}ms`} begin="indefinite" fill="freeze" calcMode="spline" keyTimes="0;1" keySplines="0.45 0.05 0.2 1">
                    <mpath href="#fx-how-return-path" />
                  </animateMotion>
                </circle>
              )}
            </svg>
          )}
          {geo && (
            <span className="fx-how-return-label" style={{ left: geo.mid.x, top: geo.mid.y }}>
              {LABEL}
            </span>
          )}
        </ol>
      </div>
    </section>
  );
}
